/**
 * Google Reviews JavaScript Module
 */

// Google Reviews configuration (will be populated by WordPress localization)
let googleReviewsConfig = window.googleReviews || {};

/**
 * Initialize Google Reviews functionality
 */
export function initGoogleReviews() {
    initToggleText();
    initLoadMore();
    initCarouselNavigation();
    animateRatingBars();
    initLazyLoading();
}

/**
 * Initialize text toggle functionality
 */
function initToggleText() {
    document.addEventListener('click', function(e) {
        if (e.target.closest('.google-review-toggle-text')) {
            e.preventDefault();
            
            const button = e.target.closest('.google-review-toggle-text');
            const reviewContent = button.closest('.google-review-content');
            const excerpt = reviewContent.querySelector('.google-review-excerpt');
            const fullText = reviewContent.querySelector('.google-review-full-text');
            
            if (fullText && fullText.style.display !== 'none') {
                // Show excerpt, hide full text
                fullText.style.display = 'none';
                if (excerpt) excerpt.style.display = 'block';
                button.textContent = 'Lire la suite';
            } else {
                // Show full text, hide excerpt
                if (excerpt) excerpt.style.display = 'none';
                if (fullText) fullText.style.display = 'block';
                button.textContent = 'Réduire';
            }
        }
    });
}

/**
 * Initialize load more functionality
 */
function initLoadMore() {
    document.addEventListener('click', function(e) {
        if (e.target.closest('.google-reviews-load-more-btn')) {
            e.preventDefault();
            
            const button = e.target.closest('.google-reviews-load-more-btn');
            const component = button.closest('.google-reviews-component');
            const container = component.querySelector('.google-reviews-container');
            const offset = parseInt(button.dataset.offset) || 0;
            const limit = 6; // Default limit
            
            // Get component settings from data attributes
            const featuredOnly = component.dataset.featuredOnly || false;
            const minRating = component.dataset.minRating || 1;
            
            // Set loading state
            button.disabled = true;
            button.textContent = 'Chargement...';
            
            // Make fetch request
            fetch(googleReviewsConfig.ajaxUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                body: new URLSearchParams({
                    action: 'load_more_google_reviews',
                    offset: offset,
                    limit: limit,
                    featured_only: featuredOnly,
                    min_rating: minRating,
                    nonce: googleReviewsConfig.nonce
                })
            })
            .then(response => response.json())
            .then(data => {
                if (data.success) {
                    // Add new reviews to container
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = data.data.html;
                    const newReviews = tempDiv.children;
                    
                    // Add new class for animation
                    Array.from(newReviews).forEach(review => {
                        review.classList.add('new');
                        container.appendChild(review);
                    });
                    
                    // Update offset
                    button.dataset.offset = offset + data.data.count;
                    
                    // Hide button if no more reviews
                    if (!data.data.has_more) {
                        button.closest('.google-reviews-load-more').style.display = 'none';
                    }
                    
                    // Trigger custom event
                    component.dispatchEvent(new CustomEvent('reviewsLoaded', {
                        detail: {
                            count: data.data.count,
                            hasMore: data.data.has_more
                        }
                    }));
                    
                } else {
                    showNotification(data.data?.message || 'Erreur lors du chargement des avis.', 'error');
                    
                    // Hide load more button
                    button.closest('.google-reviews-load-more').style.display = 'none';
                }
            })
            .catch(() => {
                showNotification('Erreur de connexion lors du chargement des avis.', 'error');
            })
            .finally(() => {
                // Reset button state
                button.disabled = false;
                button.textContent = 'Voir plus d\'avis';
            });
        }
    });
}

/**
 * Initialize carousel navigation
 */
function initCarouselNavigation() {
    const carouselComponents = document.querySelectorAll('.google-reviews-component.layout-carousel');
    
    carouselComponents.forEach(component => {
        const container = component.querySelector('.google-reviews-container');
        
        // Add navigation buttons if content overflows
        if (container && container.scrollWidth > container.clientWidth) {
            addCarouselNavigation(component);
        }
    });
}

/**
 * Add carousel navigation buttons
 */
function addCarouselNavigation(component) {
    const container = component.querySelector('.google-reviews-container');
    
    // Create navigation wrapper
    const nav = document.createElement('div');
    nav.className = 'google-reviews-carousel-nav';
    
    const prevBtn = document.createElement('button');
    prevBtn.type = 'button';
    prevBtn.className = 'carousel-btn carousel-prev';
    prevBtn.setAttribute('aria-label', 'Avis précédents');
    prevBtn.textContent = '‹';
    
    const nextBtn = document.createElement('button');
    nextBtn.type = 'button';
    nextBtn.className = 'carousel-btn carousel-next';
    nextBtn.setAttribute('aria-label', 'Avis suivants');
    nextBtn.textContent = '›';
    
    nav.appendChild(prevBtn);
    nav.appendChild(nextBtn);
    component.appendChild(nav);
    
    // Add CSS for navigation
    addCarouselStyles();
    
    // Handle navigation clicks
    prevBtn.addEventListener('click', function() {
        const scrollLeft = container.scrollLeft;
        const itemWidth = container.querySelector('.google-review-item')?.offsetWidth || 300;
        container.scrollTo({
            left: Math.max(0, scrollLeft - itemWidth * 2),
            behavior: 'smooth'
        });
    });
    
    nextBtn.addEventListener('click', function() {
        const scrollLeft = container.scrollLeft;
        const itemWidth = container.querySelector('.google-review-item')?.offsetWidth || 300;
        const maxScroll = container.scrollWidth - container.clientWidth;
        container.scrollTo({
            left: Math.min(maxScroll, scrollLeft + itemWidth * 2),
            behavior: 'smooth'
        });
    });
    
    // Update button states on scroll
    container.addEventListener('scroll', function() {
        updateCarouselNavigation(component);
    });
    
    // Initial state
    updateCarouselNavigation(component);
}

/**
 * Update carousel navigation button states
 */
function updateCarouselNavigation(component) {
    const container = component.querySelector('.google-reviews-container');
    const prevBtn = component.querySelector('.carousel-prev');
    const nextBtn = component.querySelector('.carousel-next');
    
    if (!container || !prevBtn || !nextBtn) return;
    
    const scrollLeft = container.scrollLeft;
    const maxScroll = container.scrollWidth - container.clientWidth;
    
    prevBtn.disabled = scrollLeft <= 0;
    nextBtn.disabled = scrollLeft >= maxScroll - 1;
}

/**
 * Add carousel navigation styles
 */
function addCarouselStyles() {
    if (document.getElementById('google-reviews-carousel-styles')) {
        return;
    }
    
    const styles = document.createElement('style');
    styles.id = 'google-reviews-carousel-styles';
    styles.textContent = `
        .google-reviews-carousel-nav {
            display: flex;
            justify-content: center;
            gap: 10px;
            margin-top: 20px;
        }
        
        .carousel-btn {
            background: #3b82f6;
            color: white;
            border: none;
            width: 40px;
            height: 40px;
            border-radius: 50%;
            font-size: 20px;
            cursor: pointer;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        
        .carousel-btn:hover:not(:disabled) {
            background: #1d4ed8;
            transform: scale(1.1);
        }
        
        .carousel-btn:disabled {
            background: #9ca3af;
            cursor: not-allowed;
            transform: none;
        }
    `;
    document.head.appendChild(styles);
}

/**
 * Animate rating bars on scroll
 */
function animateRatingBars() {
    // Intersection Observer for rating bars animation
    if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(function(entries) {
            entries.forEach(function(entry) {
                if (entry.isIntersecting) {
                    animateStatsOnce(entry.target);
                    observer.unobserve(entry.target);
                }
            });
        }, {
            threshold: 0.5
        });

        document.querySelectorAll('.google-reviews-stats').forEach(function(stats) {
            observer.observe(stats);
        });
    } else {
        // Fallback for browsers without Intersection Observer
        document.querySelectorAll('.google-reviews-stats').forEach(function(stats) {
            animateStatsOnce(stats);
        });
    }
}

/**
 * Animate a single stats component
 */
function animateStatsOnce(stats) {
    const fills = stats.querySelectorAll('.rating-fill');
    
    fills.forEach(function(fill) {
        const targetWidth = fill.style.width || fill.getAttribute('data-width') || '0%';
        
        // Reset width and animate
        fill.style.width = '0%';
        fill.style.transition = 'width 1s ease';
        
        setTimeout(() => {
            fill.style.width = targetWidth;
        }, 100);
    });
}

/**
 * Lazy load review images
 */
function initLazyLoading() {
    if ('IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver(function(entries) {
            entries.forEach(function(entry) {
                if (entry.isIntersecting) {
                    const img = entry.target;
                    if (img.dataset.src) {
                        img.src = img.dataset.src;
                        img.classList.remove('lazy');
                        imageObserver.unobserve(img);
                    }
                }
            });
        });

        document.querySelectorAll('.google-review-author-photo img[data-src]').forEach(function(img) {
            imageObserver.observe(img);
        });
    }
}

/**
 * Show notification
 */
function showNotification(message, type) {
    const notification = document.createElement('div');
    notification.className = `google-reviews-notification ${type}`;
    notification.textContent = message;
    
    // Add styles if not present
    if (!document.getElementById('google-reviews-notification-styles')) {
        const notificationStyles = document.createElement('style');
        notificationStyles.id = 'google-reviews-notification-styles';
        notificationStyles.textContent = `
            .google-reviews-notification {
                position: fixed;
                top: 20px;
                right: 20px;
                padding: 15px 20px;
                border-radius: 4px;
                color: white;
                font-weight: 500;
                z-index: 9999;
                animation: slideInRight 0.3s ease-out;
            }
            
            .google-reviews-notification.success {
                background: #10b981;
            }
            
            .google-reviews-notification.error {
                background: #ef4444;
            }
            
            @keyframes slideInRight {
                from {
                    transform: translateX(100%);
                    opacity: 0;
                }
                to {
                    transform: translateX(0);
                    opacity: 1;
                }
            }
        `;
        document.head.appendChild(notificationStyles);
    }
    
    document.body.appendChild(notification);
    
    // Auto remove after 3 seconds
    setTimeout(function() {
        notification.style.opacity = '0';
        setTimeout(() => {
            if (notification.parentNode) {
                notification.parentNode.removeChild(notification);
            }
        }, 300);
    }, 3000);
}

/**
 * Utility function to format numbers
 */
export function formatNumber(num) {
    if (num >= 1000000) {
        return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'k';
    }
    return num.toString();
}

// Make functions globally available for backward compatibility
if (typeof window !== 'undefined') {
    window.GoogleReviews = {
        init: initGoogleReviews,
        loadMore: function(component) {
            const button = component.querySelector('.google-reviews-load-more-btn');
            if (button) {
                button.click();
            }
        },
        showNotification: showNotification,
        formatNumber: formatNumber
    };
}
